import { ArrowLeft, Eye } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { legalService } from "@/api/legal";
import { TLegalDocumentFormValues } from "@/api/legal/legal.types";
import {
  LEGAL_DOCUMENT_CREATE_FORM_ID,
  LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT,
  LegalDocumentForm,
  LegalDocumentPreviewDialog,
} from "@/components/admin/legal";
import { dateInputToIso } from "@/components/admin/legal/constants";
import { AdminPageLayout } from "@/components/layout/AdminPageLayout";
import { AppCard } from "@/components/ui/AppCard";
import { Button, RouteLink, useToast } from "@/components/ui";
import { extractErrorMessage } from "@/utils";

export function LegalDocumentCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewContent, setPreviewContent] = useState("");
  const [previewVersion, setPreviewVersion] = useState("");
  const [previewEffectiveAt, setPreviewEffectiveAt] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const handleSubmit = async (values: TLegalDocumentFormValues) => {
    setIsSubmitting(true);
    const { error, data } = await legalService.createDocument({
      document_type: values.document_type,
      version: values.version,
      title: values.title,
      content: values.content,
      effective_at: dateInputToIso(values.effective_at),
      is_active: values.is_active ?? false,
    });
    setIsSubmitting(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not create document",
        description: await extractErrorMessage(error),
      });
      return;
    }

    toast({ variant: "success", title: "Legal document version created" });
    const documentId = data?.data?.id;
    if (documentId) {
      void navigate({ to: "/app/admin/legal/$document_id", params: { document_id: documentId } });
    } else {
      void navigate({ to: "/app/admin/legal" });
    }
  };

  return (
    <AdminPageLayout
      title="Create legal document"
      maxWidth="7xl"
      actions={
        <RouteLink to="/app/admin/legal">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to documents
          </Button>
        </RouteLink>
      }
    >
      <AppCard
        variant="elevated"
        padding="none"
        className={`flex min-h-0 flex-col overflow-hidden ${LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT}`}
      >
        <div
          className="sticky top-0 z-10 flex shrink-0 items-center justify-end gap-2 border-b border-border bg-card px-6 py-3 sm:px-8"
        >
          <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(true)}>
            <Eye className="mr-2 h-4 w-4" />
            Live preview
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: "/app/admin/legal" })}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={LEGAL_DOCUMENT_CREATE_FORM_ID}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Create version
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 py-5 sm:px-8 sm:py-6">
          <LegalDocumentForm
            mode="create"
            fillEditor
            formId={LEGAL_DOCUMENT_CREATE_FORM_ID}
            onSubmit={handleSubmit}
            onPreviewChange={({ title, content, version, effective_at }) => {
              setPreviewTitle(title);
              setPreviewContent(content);
              if (typeof version === "string") setPreviewVersion(version);
              if (typeof effective_at === "string") setPreviewEffectiveAt(effective_at);
            }}
            isSubmitting={isSubmitting}
          />
        </div>
      </AppCard>

      <LegalDocumentPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        title={previewTitle}
        content={previewContent}
        version={previewVersion || undefined}
        effectiveAt={previewEffectiveAt ? dateInputToIso(previewEffectiveAt) : undefined}
      />
    </AdminPageLayout>
  );
}
